/**
 * The banking list route the web and the phone both call (T3214). 🔨 Relative to
 * the API's own root, so the path never repeats the base's `/api`.
 */
export const BANK_ACCOUNTS_PATH = '/banking/accounts';

/**
 * 🔒 The bank account's feed list, Uncategorized tab and All transactions read
 * this many lines a page, and Show more reads the next page (decision 9760).
 */
export const BANK_LINES_PAGE = 200;

/** 🔒 The Uncategorized tab's two orders (decision 9760): newest first by default, oldest first to bring up the lines a reconciler missed. */
export const BANK_LINE_ORDERS = ['newest', 'oldest'] as const;
export type BankLineOrder = (typeof BANK_LINE_ORDERS)[number];

/** What each of the three reads answers beside its rows: every line the read matches, and how many are past this page. */
export interface BankLinesCount {
  total: number;
  left: number;
}

/** The page parameters of one of the three reads; an offset of 0 and the newest first add nothing, so the first page's address is the one it always was. */
export function bankLinesQuery(offset = 0, order: BankLineOrder = 'newest'): string {
  const query = new URLSearchParams();
  if (offset > 0) query.set('offset', String(Math.floor(offset)));
  if (order === 'oldest') query.set('order', 'oldest');
  return query.toString();
}

/** 🔒 The furthest line a read may start at (T3460): past a million lines the read starts at the first page, so a huge offset never reaches the database. */
export const BANK_LINES_OFFSET_MAX = 1_000_000;

/** The offset a read was asked for: a whole number of lines from 0 to the cap, anything else reading as the first page. */
export function bankLinesOffset(raw: string | undefined): number {
  const asked = Number(raw);
  return Number.isSafeInteger(asked) && asked > 0 && asked <= BANK_LINES_OFFSET_MAX ? asked : 0;
}

/** The order a read was asked for: `oldest` exactly, anything else reading as the newest first. */
export function bankLineOrder(raw: string | undefined): BankLineOrder {
  return raw === 'oldest' ? 'oldest' : 'newest';
}

/** The count a page answers: the total, and how many lie past the lines this page and those before it hold. */
export function bankLinesCount(total: number, offset: number, shown: number): BankLinesCount {
  return { total, left: Math.max(0, total - offset - shown) };
}
